import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import logger from "../utils/logger";

class WebSocketService {
  constructor() {
    this.client = null;
    this.connected = false;
    this.subscriptions = new Map(); // topic -> STOMP subscription
    this.callbacks = new Map(); // topic -> Map(callbackId -> callback)
    this.callbackIdCounter = 0;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.shouldReconnect = true;
    this.baseUrl = null;
  }

  connect(baseUrl) {
    if (this.connected) {
      logger.log("WebSocket already connected");
      return Promise.resolve();
    }

    this.baseUrl = baseUrl;
    this.shouldReconnect = true;
    return new Promise((resolve, reject) => {
      try {
        const httpUrl = baseUrl.replace(/\/$/, "") + "/booking/api/v2/ws";
        // Always get fresh token from localStorage
        const token = localStorage.getItem("access_token");
        logger.log("🔌 Connecting to booking WebSocket:", httpUrl);

        this.client = new Client({
          webSocketFactory: () => new SockJS(httpUrl),
          connectHeaders: {
            Authorization: token ? `Bearer ${token}` : "",
          },
          reconnectDelay: 0,
          heartbeatIncoming: 4000,
          heartbeatOutgoing: 4000,
          onConnect: () => {
            this.connected = true;
            this.reconnectAttempts = 0;
            logger.log("✅ WebSocket connected successfully with auth");
            // Resubscribe to all topics after reconnection
            this.resubscribeAll();
            resolve();
          },
          onStompError: frame => {
            logger.error("❌ STOMP error:", frame);
            this.connected = false;
            reject(new Error("WebSocket connection failed"));
          },
          onWebSocketError: error => {
            logger.error("❌ WebSocket error:", error);
            this.connected = false;
          },
          onWebSocketClose: () => {
            this.connected = false;
            logger.log("WebSocket connection closed");
            if (this.shouldReconnect) {
              this.handleReconnect();
            }
          },
        });

        this.client.activate();
      } catch (error) {
        logger.error("Failed to initialize WebSocket:", error);
        reject(error);
      }
    });
  }

  handleReconnect() {
    if (!this.shouldReconnect || !this.baseUrl) return;

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      logger.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      setTimeout(() => {
        if (this.shouldReconnect) {
          this.connect(this.baseUrl).catch(() => {
            logger.error("Reconnection failed");
          });
        }
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      logger.log("⚠️ Max reconnection attempts reached. WebSocket unavailable.");
    }
  }

  /**
   * Resubscribe to all topics after reconnection
   * This ensures callbacks are still notified after reconnect
   */
  resubscribeAll() {
    if (!this.client || !this.connected) return;

    // Clear old STOMP subscriptions (they're invalid after reconnect)
    this.subscriptions.clear();

    // Resubscribe to all topics that have callbacks
    this.callbacks.forEach((callbackMap, topic) => {
      if (callbackMap.size > 0) {
        this.createStompSubscription(topic);
      }
    });
  }

  /**
   * Create or get existing STOMP subscription for a topic
   */
  createStompSubscription(topic) {
    if (!this.client || !this.connected) {
      logger.error("Cannot subscribe: WebSocket not connected");
      return null;
    }

    // If subscription already exists, return it
    if (this.subscriptions.has(topic)) {
      return this.subscriptions.get(topic);
    }

    try {
      const subscription = this.client.subscribe(topic, message => {
        try {
          const data = JSON.parse(message.body);
          logger.log(`📦 WebSocket message on ${topic}:`, data);
          // Notify all callbacks for this topic
          const topicCallbacks = this.callbacks.get(topic);
          if (topicCallbacks) {
            topicCallbacks.forEach(callback => {
              try {
                callback(data);
              } catch (err) {
                logger.error(`Error in WebSocket callback for ${topic}:`, err);
              }
            });
          }
        } catch (error) {
          logger.error("Failed to parse WebSocket message:", error);
        }
      });

      this.subscriptions.set(topic, subscription);
      logger.log(`Created STOMP subscription for ${topic}`);
      return subscription;
    } catch (error) {
      logger.error(`Failed to subscribe to ${topic}:`, error);
      return null;
    }
  }

  subscribeToAllBookings(callback) {
    return this.subscribe("/topic/bookings", callback);
  }

  subscribeToBooking(bookingId, callback) {
    return this.subscribe(`/topic/bookings/${bookingId}`, callback);
  }

  subscribeToOrganizationBookings(orgId, callback) {
    return this.subscribe(`/topic/organizations/${orgId}/bookings`, callback);
  }

  subscribeToUserQueue(callback) {
    return this.subscribe("/user/queue/bookings", callback);
  }

  /**
   * Subscribe to a topic with a callback
   * Returns a callback ID that can be used to unsubscribe this specific callback
   */
  subscribe(topic, callback) {
    // Initialize callback map for this topic if needed
    if (!this.callbacks.has(topic)) {
      this.callbacks.set(topic, new Map());
    }

    // Generate unique callback ID
    const callbackId = ++this.callbackIdCounter;
    this.callbacks.get(topic).set(callbackId, callback);
    logger.log(`Added callback ${callbackId} to ${topic}`);

    // Create STOMP subscription if connected and not already subscribed
    if (this.connected) {
      this.createStompSubscription(topic);
    }

    // Return an object with unsubscribe method for this specific callback
    return {
      id: callbackId,
      topic,
      unsubscribe: () => this.unsubscribeCallback(topic, callbackId),
    };
  }

  /**
   * Update an existing callback
   */
  updateCallback(topic, callbackId, newCallback) {
    const topicCallbacks = this.callbacks.get(topic);
    if (topicCallbacks && topicCallbacks.has(callbackId)) {
      topicCallbacks.set(callbackId, newCallback);
      logger.log(`Updated callback ${callbackId} for ${topic}`);
      return true;
    }
    return false;
  }

  /**
   * Unsubscribe a specific callback from a topic
   */
  unsubscribeCallback(topic, callbackId) {
    const topicCallbacks = this.callbacks.get(topic);
    if (topicCallbacks) {
      topicCallbacks.delete(callbackId);
      logger.log(`Removed callback ${callbackId} from ${topic}`);

      // If no more callbacks for this topic, unsubscribe from STOMP
      if (topicCallbacks.size === 0) {
        this.unsubscribeTopic(topic);
      }
    }
  }

  /**
   * Unsubscribe from a topic entirely (removes STOMP subscription)
   */
  unsubscribeTopic(topic) {
    const subscription = this.subscriptions.get(topic);
    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(topic);
      logger.log(`Unsubscribed STOMP subscription from ${topic}`);
    }
    this.callbacks.delete(topic);
  }

  /**
   * Legacy method - unsubscribe by topic name
   */
  unsubscribe(topic) {
    this.unsubscribeTopic(topic);
  }

  unsubscribeAll() {
    this.subscriptions.forEach((subscription, topic) => {
      subscription.unsubscribe();
      logger.log(`Unsubscribed from ${topic}`);
    });
    this.subscriptions.clear();
    this.callbacks.clear();
  }

  disconnect() {
    this.shouldReconnect = false;
    if (this.client) {
      this.unsubscribeAll();
      this.client.deactivate();
      this.connected = false;
      logger.log("WebSocket disconnected");
    }
  }

  isConnected() {
    return this.connected;
  }
}

export default new WebSocketService();
